/**
 * HomeComposer: the Home "Ask Lyra" box. It is the real chat (the same engine as New Task), so the answer appears here, with the
 * agent, tool, file and knowledge pickers in its toolbar.
 *
 * Conversations stay on Home: the expand button makes the chat fill the visible window, "Conversations" opens the saved-chat list
 * (folders, search, rename, move: the New Task rail), and the "Save conversations" switch decides whether a chat is kept at all.
 * It is off until the user turns it on, and only then is anything written to their history.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { History, Maximize2, Minimize2, Plus } from 'lucide-react';
import { T2 } from './AppShellV2.tsx';
import RunDrawer from '../agentsWorkflows/RunDrawer.jsx';
import ChatHistoryRail from './ChatHistoryRail.tsx';
import { customAgentAPI } from '../../services/api.js';
import { taskService } from '../../services/taskApi.ts';
import AddTaskModal from '../modals/AddTaskModal.tsx';
import toast from 'react-hot-toast';
import type { DomainAgentSelection } from '../../hooks/useDomainAgentSelection.ts';

const OPEN = 520;          // box height once a conversation has started (not expanded)
const SAVE_KEY = 'lyra:home:saveConversations';
const SESSION_KEY = 'lyra:home:session';   // the open chat, kept for this browser tab until the user starts a new chat
const readSession = () => { try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; } };
const writeSession = (id: string | null) => { try { if (id) sessionStorage.setItem(SESSION_KEY, id); else sessionStorage.removeItem(SESSION_KEY); } catch { /* not remembered */ } };

/** The visible content area (right of the sidebar, below the top bar), so the expanded chat fits the window and nothing else. */
function useContentFrame(active: boolean) {
  const [frame, setFrame] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!active) { setFrame(null); return undefined; }
    const measure = () => {
      const main = document.querySelector('main');
      const r = main ? main.getBoundingClientRect() : { top: 56, left: 0, width: window.innerWidth, height: window.innerHeight - 56 };
      const top = Math.max(r.top, 0) + 8;
      setFrame({ top, left: r.left + 8, width: r.width - 16, height: window.innerHeight - top - 8 });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active]);
  return frame;
}

function Switch({ on, onChange, label, hint }: { on: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} title={hint} onClick={() => onChange(!on)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', padding: '4px 6px', color: T2.ink2, fontSize: 12.5, fontFamily: 'inherit' }}>
      <span style={{ width: 30, height: 17, borderRadius: 99, background: on ? 'var(--lyra-color-primary)' : 'var(--app-border-strong)', position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: 99, background: 'var(--lyra-color-surface)', transition: 'left .15s' }} />
      </span>
      <span>{label}</span>
    </button>
  );
}

const iconBtn = (active = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
  border: `1px solid ${active ? 'var(--app-primary-soft-2)' : T2.border}`, background: active ? 'var(--app-primary-soft)' : T2.surface, color: active ? 'var(--app-primary-deep)' : T2.ink2,
});

export default function HomeComposer({ selection, externalAgentIds = [] }: { selection: DomainAgentSelection; externalAgentIds?: string[] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const [agents, setAgents] = useState<any[]>([]);
  const [started, setStarted] = useState(!!sessionId);
  const [expanded, setExpanded] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [railKey, setRailKey] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [save, setSave] = useState(() => { try { return localStorage.getItem(SAVE_KEY) === 'true'; } catch { return false; } });
  const frame = useContentFrame(expanded);
  const [editTask, setEditTask] = useState<any>(null);   // a task opened from a chat reply, edited in place
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);   // where the chat's Context / copy / refresh buttons go

  // Keep the conversation across page changes and refreshes: remember the open session, and when Home is opened without one
  // (a sidebar click, a typed URL) go back to it. It only ends with an explicit "New chat" (the URL param is then removed).
  const restored = useRef(false);
  useEffect(() => {
    if (!restored.current) {
      restored.current = true;
      const remembered = readSession();
      if (!sessionId && remembered && save) {
        setStarted(true);
        setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('session', remembered); return next; }, { replace: true });
        return;
      }
    }
    writeSession(sessionId);
  }, [sessionId, save, setSearchParams]);
  useEffect(() => { try { localStorage.setItem(SAVE_KEY, String(save)); } catch { /* the choice is just not remembered */ } }, [save]);
  useEffect(() => {
    customAgentAPI.list().then((res: any) => setAgents(Array.isArray(res) ? res : res?.agents || [])).catch(() => setAgents([]));
  }, []);
  // The first send opens the box up to show the conversation.
  useEffect(() => {
    const open = () => setStarted(true);
    window.addEventListener('lyra:run-start', open);
    return () => window.removeEventListener('lyra:run-start', open);
  }, []);
  // A task card in a reply opens the edit modal here, without leaving Home.
  useEffect(() => {
    const onEdit = async (e: Event) => {
      const { taskId, task } = (e as CustomEvent).detail || {};
      if (!taskId) return;
      try { setEditTask(await taskService.getTaskById(taskId)); }
      catch { if (task) setEditTask({ ...task, _id: taskId }); else toast.error('Could not open that task'); }
    };
    window.addEventListener('lyra:edit-task', onEdit);
    return () => window.removeEventListener('lyra:edit-task', onEdit);
  }, []);
  useEffect(() => {
    if (!expanded) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !(e.target as HTMLElement)?.closest?.('[role=dialog]')) setExpanded(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [expanded]);

  const lyranAgent = useMemo(() => agents.find((a) => a.isLyran) || null, [agents]);

  const onSessionSaved = useCallback(() => setRailKey((k) => k + 1), []);
  const selectSession = useCallback((id: string) => {
    setStarted(true);
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('session', id); return next; }, { replace: true });
  }, [setSearchParams]);
  const newChat = useCallback(() => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('session'); return next; }, { replace: true });
    setResetKey((k) => k + 1);
  }, [setSearchParams]);
  const openConversations = () => { setExpanded(true); setRailOpen((o) => (expanded ? !o : true)); };

  const cardStyle: React.CSSProperties = expanded && frame
    ? { position: 'fixed', top: frame.top, left: frame.left, width: frame.width, height: frame.height, zIndex: 45, boxShadow: '0 24px 60px rgba(8,12,24,.35)' }
    : { position: 'relative', width: '100%' };

  return (
    <>
      {expanded && <div style={{ height: 120 }} aria-hidden="true" />}
      <div style={{ background: T2.surface, border: `1px solid ${T2.border}`, borderRadius: 14, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'visible', ...cardStyle }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px 4px 14px', flexShrink: 0, flexWrap: 'wrap' }}>
          <button type="button" onClick={newChat} title="Start a new chat" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: T2.ink, fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', padding: '5px 6px', borderRadius: 7 }}>
            <Plus size={16} /> New chat
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div ref={setSlot} style={{ display: 'flex', alignItems: 'center' }} />
            <Switch on={save} onChange={setSave} label="Save conversations" hint={save ? 'Chats are kept in your history' : 'Chats are not saved. Turn on to keep them in your history'} />
            <button type="button" onClick={openConversations} aria-pressed={railOpen && expanded} style={iconBtn(railOpen && expanded)} title="Saved conversations">
              <History size={14} /> Conversations
            </button>
            <button type="button" onClick={() => setExpanded((e) => !e)} aria-label={expanded ? 'Collapse chat' : 'Expand chat'} title={expanded ? 'Collapse' : 'Fill the window'} style={{ ...iconBtn(), width: 30, padding: 0, justifyContent: 'center' }}>
              {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', flex: expanded ? 1 : undefined, minHeight: 0, padding: '0 8px 8px' }}>
          {expanded && railOpen && (
            <div style={{ display: 'flex', flexShrink: 0, minHeight: 0, borderRadius: 12, overflow: 'hidden', border: `1px solid ${T2.border}`, marginRight: 10 }}>
              <ChatHistoryRail activeSessionId={sessionId} refreshKey={railKey} onNewChat={newChat} onSelect={selectSession} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, height: expanded ? '100%' : started ? OPEN : 'auto', transition: expanded ? undefined : 'height .2s ease', borderRadius: 12, position: 'relative' }}>
            <RunDrawer
              variant="page"
              target={lyranAgent}
              targetType="agent"
              gmailStatus={null}
              agents={agents}
              spaceId={null}
              compact={!expanded}
              dense
              headerTarget={slot}
              wideChat={expanded}
              showEmptyState={expanded}
              pageHeading="What do you want to get done?"
              pageSubheading={save ? 'This conversation will be saved to your history.' : 'This conversation is not saved. Turn on "Save conversations" to keep it.'}
              autosave={save}
              showNewTask={false}
              resetKey={resetKey}
              onSessionSaved={onSessionSaved}
              domainAgents={selection.selectedAgents}
              availableDomainAgents={selection.available}
              onAddDomainAgent={selection.toggle}
              onRemoveDomainAgent={selection.remove}
              externalAgentIds={externalAgentIds}
            />
          </div>
        </div>
      </div>
      {editTask && (
        <AddTaskModal
          isOpen
          mode="task-management"
          task={editTask}
          onClose={() => setEditTask(null)}
          onTaskAdded={() => setEditTask(null)}
        />
      )}
    </>
  );
}
